import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

const STORAGE_KEY = "sidebar-collapsed";

type SidebarCollapseState = {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
};

const SidebarCollapseContext = createContext<SidebarCollapseState | null>(null);

/**
 * Provider component to wrap the app and share sidebar state
 */
export const SidebarCollapseProvider = ({ children }: { children: React.ReactNode }) => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    // Default to collapsed state for cleaner UX
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      // If no stored value, default to collapsed (true)
      if (stored === null) {
        return true;
      }
      // Parse stored value
      return stored === "true";
    } catch {
      // If localStorage fails, default to collapsed
      return true;
    }
  });

  // Use useCallback to create stable function references
  const toggle = useCallback(() => {
    setIsCollapsed((prev) => {
      const newValue = !prev;
      // Persist to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, String(newValue));
      } catch {
        // localStorage unavailable
        console.error("[SidebarCollapseProvider] toggle: localStorage unavailable");
      }
      return newValue;
    });
  }, [isCollapsed]);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(() => {
      // Persist to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, String(collapsed));
      } catch {
        // localStorage unavailable
        console.error("[SidebarCollapseProvider] setCollapsed: localStorage unavailable");
      }
      return collapsed;
    });
  }, []);

  // Memoize the state object to prevent unnecessary re-renders
  const sidebarState = useMemo(() => ({ isCollapsed, toggle, setCollapsed }), [isCollapsed, toggle, setCollapsed]);

  return <SidebarCollapseContext.Provider value={sidebarState}>{children}</SidebarCollapseContext.Provider>;
};

/**
 * Hook to manage sidebar collapse state with localStorage persistence
 */
export const useSidebarCollapse = (): SidebarCollapseState => {
  const context = useContext(SidebarCollapseContext);

  if (!context) {
    // This should not happen if SidebarCollapseProvider is properly wrapped
    // Fallback to creating state (for development/debugging)
    console.error("SidebarCollapseContext not found. Make sure App.tsx wraps children with SidebarCollapseProvider.");
    throw new Error("useSidebarCollapse must be used within SidebarCollapseProvider");
  }

  return context;
};
